<template>
  <div class="promo-design-inputs">
    <v-expansion-panels v-model="getPromoPanel" multiple focusable>
      <v-expansion-panel>
        <v-expansion-panel-title>Message</v-expansion-panel-title>
        <v-expansion-panel-text class="retention-config-segments">
          <RfRetentionActionsTitleMessage :model="model" :actions="actions" :app="app" noStyle />
        </v-expansion-panel-text>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-title>Image</v-expansion-panel-title>
        <v-expansion-panel-text class="retention-config-segments">
          <div class="flex flex-col gap-2">
            <v-checkbox
              v-model="actions.rf_settings_bg_image_is_url"
              label="Input image url"
              class="background-img-checkbox"
              hideDetails
              trueValue="true"
              falseValue="false"
            />
            <div v-if="actions.rf_settings_bg_image_is_url === 'true'" class="upload-bgimg-wrapper">
              <v-text-field
                v-model="actions.rf_push_ios_image_url"
                variant="outlined"
                density="compact"
                label="Image iOS Url"
                name="video-url"
                persistentPlaceholder
                hideDetails
              ></v-text-field>
              <div class="upload-img-helper">1:1 aspect ratio</div>
            </div>
            <div v-else class="upload-bgimg-wrapper">
              <v-btn
                variant="flat"
                color="accent"
                class="upload-image-btn"
                @click="iosImageInput?.click?.()"
              >
                <v-icon size="14" start>fa:fas fa-upload</v-icon>Upload iOS image
              </v-btn>
              <input
                ref="iosImageInput"
                type="file"
                class="hidden"
                accept="image/*"
                @change="uploadBgImageFromInput('pushIos')"
              />
              <RfImageFileInfo
                :image="actions.rf_push_ios_image"
                :filename="actions.rf_push_ios_image_filename"
              />
              <div class="upload-img-helper">1:1 aspect ratio</div>
            </div>
            <div v-if="actions.rf_settings_bg_image_is_url === 'true'" class="upload-bgimg-wrapper">
              <v-text-field
                v-model="actions.rf_settings_bg_image_url"
                variant="outlined"
                density="compact"
                label="Image Android Url"
                name="video-url"
                persistentPlaceholder
                hideDetails
              ></v-text-field>
              <div class="upload-img-helper">2:1 aspect ratio</div>
            </div>
            <div v-else class="upload-bgimg-wrapper">
              <v-btn
                variant="flat"
                color="accent"
                class="upload-image-btn"
                @click="androidImageInput?.click?.()"
              >
                <v-icon size="14" start>fa:fas fa-upload</v-icon>Upload android image
              </v-btn>
              <input
                ref="androidImageInput"
                type="file"
                class="hidden"
                accept="image/*"
                @change="uploadBgImageFromInput('pushAndroid')"
              />
              <RfImageFileInfo
                :image="actions.rf_settings_bg_image"
                :filename="actions.rf_settings_bg_image_filename"
              />
              <div class="upload-img-helper">2:1 aspect ratio</div>
            </div>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-title>Action</v-expansion-panel-title>
        <v-expansion-panel-text class="retention-config-segments">
          <div class="flex flex-col gap-2">
            <v-select
              v-model.trim="actions.rf_push_action"
              variant="outlined"
              density="compact"
              hideDetails
              :items="[
                { value: 'OPEN_APP', text: 'Open app' },
                { value: 'DEEP_LINK', text: 'Open app to deep link' },
                { value: 'IN_APP_PURCHASE', text: 'Open app to in-app purchase' },
                { value: 'URL', text: 'Open url' },
              ]"
              itemTitle="text"
              label="Action"
              class="mb-4"
            />
            <div v-if="actions.rf_push_action === 'OPEN_APP'">
              <v-select
                v-model.trim="actions.rf_settings_apple_prompt_on_open"
                variant="outlined"
                density="compact"
                hideDetails
                :items="[{ name: '(none)', slug: null }, ...iosPrompts]"
                :disabled="allPromptsStore.getPromptsLoading"
                itemTitle="name"
                itemValue="slug"
                :label="`iOS prompt on open ${allPromptsStore.getPromptsLoading ? '(Loading...)' : ''}`"
                class="mb-6"
              />
              <v-select
                v-model.trim="actions.rf_settings_android_prompt_on_open"
                variant="outlined"
                density="compact"
                hideDetails
                :items="[{ name: '(none)', slug: null }, ...androidPrompts]"
                :disabled="allPromptsStore.getPromptsLoading"
                itemTitle="name"
                itemValue="slug"
                :label="`Android prompt on open ${allPromptsStore.getPromptsLoading ? '(Loading...)' : ''}`"
              />
            </div>
            <v-text-field
              v-if="['URL', 'DEEP_LINK'].includes(actions.rf_push_action)"
              v-model.trim="actions.rf_settings_deeplink"
              variant="outlined"
              persistentPlaceholder
              density="compact"
              hideDetails
              label="Link"
            />
            <div v-if="actions.rf_push_action === 'IN_APP_PURCHASE'">
              <RfRetentionActionsInapp :actions="actions" :app="app" deviceType="ios" />
              <RfRetentionActionsInapp :actions="actions" :app="app" deviceType="android_os" />
            </div>
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
import { ref, watchEffect, computed, onMounted } from "vue";
import RfRetentionActionsTitleMessage from "@/components/RfPathCreate/RfRetentionActions/RfRetentionActionsTitleMessage.vue";
import UploadImageMixin from "@/utils/UploadImageMixin";
import RfImageFileInfo from "@/components/RfPathCreate/RfRetentionActions/components/RfImageFileInfo.vue";
import RfRetentionActionsInapp from "@/components/RfPathCreate/RfRetentionActions/RfRetentionActionsInapp.vue";
import { useAllPromptsStore } from "@/pinia/allPromptsStore";

export default {
  components: { RfRetentionActionsTitleMessage, RfImageFileInfo, RfRetentionActionsInapp },
  mixins: [UploadImageMixin],
  props: ["actions", "model", "app"],
  setup: props => {
    const allPromptsStore = useAllPromptsStore();
    const getPromoPanel = ref([0]);
    const iosImageInput = ref();
    const androidImageInput = ref();

    const iosPrompts = computed(() =>
      allPromptsStore.sortedAllPrompts.filter(
        ({ path_type, device_type }) => path_type === "retention_modal" && device_type === "ios",
      ),
    );
    const androidPrompts = computed(() =>
      allPromptsStore.sortedAllPrompts.filter(
        ({ path_type, device_type }) =>
          path_type === "retention_modal" && device_type === "android_os",
      ),
    );

    onMounted(() => {
      if (!allPromptsStore.prompts.length)
        allPromptsStore.getPrompts(props.app.id, { nonBlocking: true });
    });

    watchEffect(() => {
      if (!["URL", "DEEP_LINK"].includes(props.actions.rf_push_action))
        props.actions.rf_settings_deeplink = null;
      if (props.actions.rf_push_action !== "IN_APP_PURCHASE")
        props.actions.rf_settings_apple_inapp_product_id =
          props.actions.rf_settings_android_inapp_product_id = null;
      if (props.actions.rf_push_action !== "OPEN_APP")
        props.actions.rf_settings_apple_prompt_on_open =
          props.actions.rf_settings_android_prompt_on_open = null;
    });

    return {
      getPromoPanel,
      iosImageInput,
      androidImageInput,
      iosPrompts,
      androidPrompts,
      allPromptsStore,
    };
  },
};
</script>
